// v50.264 — Site Ops verification ledger API.
//
// Stage 1 of the labourcore-site-ops-ledger-build brief. Implements the
// status spine (draft → submitted → confirmed/flagged →
// deemed_confirmed → locked) on the existing daily_logs table.
//
// Reuses:
//   • lcAudit fire-and-forget into audit_events for transition stamps
//   • checkOperativeVerifiedStatus for the compliance auto-snapshot on
//     submit + confirm (never re-keyed)
//   • Direct supabase calls (the legacy LABOURCORE_API.daily_logs paths
//     stay untouched for backward compat — older callers keep working)
//   • crypto.subtle.digest("SHA-256", ...) — same pattern as
//     BuildContractOfferModal.sha256Hex

import { supabase } from "@/lib/api/supabase-client";
import { lcAudit } from "@/lib/audit";
import { checkOperativeVerifiedStatus } from "@/features/compliance/useOperativeVerifiedStatus";

export type LogStatus =
  | "draft"
  | "submitted"
  | "confirmed"
  | "flagged"
  | "deemed_confirmed"
  | "locked";

export interface DailyLogRow {
  id:                       string;
  placement_id:             string;
  log_date:                 string;
  hours_worked:             number | null;
  notes:                    string | null;
  status:                   LogStatus;
  planned_hours:            number | null;
  planned_headcount:        number | null;
  actual_headcount:         number | null;
  variance_pct:             number | null;
  variance_reason:          string | null;
  flag_reason:              string | null;
  version:                  number;
  sha256_hash:              string | null;
  submitted_at:             string | null;
  submitted_by_user_id:     string | null;
  confirmed_at:             string | null;
  confirmed_by_user_id:     string | null;
  deemed_confirmed_at:      string | null;
  locked_at:                string | null;
  compliance_lapsed:        boolean;
  compliance_lapse_summary: string | null;
  created_at:               string;
}

const VARIANCE_THRESHOLD_PCT = 10;

export function variancePct(planned: number, actual: number): number {
  if (planned <= 0) return 0;
  return Math.round(((actual - planned) / planned) * 100 * 100) / 100;
}

export function isVarianceOverThreshold(planned: number, actual: number): boolean {
  return Math.abs(variancePct(planned, actual)) > VARIANCE_THRESHOLD_PCT;
}

/* SHA-256 of an arbitrary string. Same crypto.subtle pattern as the
   v49.26 BuildContractOfferModal.sha256Hex. */
export async function sha256Hex(input: string): Promise<string> {
  const enc = new TextEncoder().encode(input);
  const buf = await crypto.subtle.digest("SHA-256", enc);
  return Array.from(new Uint8Array(buf))
    .map(b => b.toString(16).padStart(2, "0"))
    .join("");
}

/* Build the deterministic snapshot string we hash before stamping a
   confirm / lock. Stable key order is critical so the same row produces
   the same hash on confirm + later verification. */
function snapshotString(r: Partial<DailyLogRow>): string {
  return JSON.stringify({
    placement_id:      r.placement_id,
    log_date:          r.log_date,
    hours_worked:      r.hours_worked,
    planned_hours:     r.planned_hours,
    planned_headcount: r.planned_headcount,
    actual_headcount:  r.actual_headcount,
    variance_reason:   r.variance_reason,
    notes:             r.notes,
    version:           r.version,
    status:            r.status,
  });
}

/* Append a versions row. Failure is logged but doesn't bubble — the
   primary transition has already landed; missing a versions row is a
   recoverable audit deficiency. */
async function writeVersion(args: {
  log_id:        string;
  version:       number;
  action:        "submit" | "confirm" | "flag" | "amend" | "deem_confirm" | "lock";
  snapshot:      Partial<DailyLogRow>;
  reason?:       string | null;
  actor_user_id: string | null;
}): Promise<void> {
  try {
    const snap = JSON.stringify(args.snapshot);
    const hash = await sha256Hex(snap);
    await supabase.from("daily_log_versions").insert({
      log_id:        args.log_id,
      version:       args.version,
      action:        args.action,
      snapshot:      args.snapshot,
      sha256:        hash,
      reason:        args.reason ?? null,
      actor_user_id: args.actor_user_id,
    });
  } catch (e) {
    console.warn("[dailyLogs.writeVersion] non-fatal:", e);
  }
}

/* Compute the compliance snapshot for an operative on a log date.
   Pure read of the wallet — never re-keys credentials. Returns the
   lapse summary string if any required cert is missing/expired, or
   null if the operative is verified. */
export async function computeComplianceSnapshot(operativeId: string | null | undefined): Promise<{
  lapsed:  boolean;
  summary: string | null;
}> {
  if (!operativeId) return { lapsed: false, summary: null };
  try {
    const result = await checkOperativeVerifiedStatus(operativeId);
    if (result.status === "verified") return { lapsed: false, summary: null };
    return {
      lapsed:  true,
      summary: result.gapSummary || result.status,
    };
  } catch (e) {
    console.warn("[dailyLogs.computeComplianceSnapshot] failed:", e);
    return { lapsed: false, summary: null };
  }
}

interface SubmitInput {
  placement_id:      string;
  operative_id?:     string | null;
  log_date:          string;
  planned_hours:     number;
  planned_headcount: number;
  actual_hours:      number;
  actual_headcount:  number;
  variance_reason?:  string | null;
  notes?:            string | null;
}

/* Partner submit. Pre-fill driven — the caller passes planned values
   from the allocation alongside actual values from the partner. */
export async function submitLog(input: SubmitInput): Promise<DailyLogRow | null> {
  const sessionRes = await supabase.auth.getSession();
  const userId = sessionRes?.data?.session?.user?.id ?? null;

  const compliance = await computeComplianceSnapshot(input.operative_id);
  const nowIso = new Date().toISOString();

  const insertRow = {
    placement_id:             input.placement_id,
    log_date:                 input.log_date,
    hours_worked:             input.actual_hours,
    planned_hours:            input.planned_hours,
    planned_headcount:        input.planned_headcount,
    actual_headcount:         input.actual_headcount,
    variance_reason:          input.variance_reason ?? null,
    notes:                    input.notes ?? null,
    status:                   "submitted" as const,
    version:                  1,
    submitted_by_user_id:     userId,
    submitted_at:             nowIso,
    compliance_lapsed:        compliance.lapsed,
    compliance_lapse_summary: compliance.summary,
  };

  const { data, error } = await supabase
    .from("daily_logs")
    .insert(insertRow)
    .select("*")
    .single();
  if (error || !data) {
    console.warn("[dailyLogs.submitLog]:", error);
    return null;
  }
  const row = data as DailyLogRow;

  await writeVersion({
    log_id:        row.id,
    version:       1,
    action:        "submit",
    snapshot:      row,
    actor_user_id: userId,
  });

  lcAudit({
    event_type: "daily_log.submitted",
    ui_string:  `Submitted ${input.actual_hours}h log for ${input.log_date}`,
    metadata:   {
      log_id:           row.id,
      placement_id:     input.placement_id,
      log_date:         input.log_date,
      planned_hours:    input.planned_hours,
      actual_hours:     input.actual_hours,
      variance_pct:     row.variance_pct,
      compliance_lapse: compliance.summary,
    },
  });

  return row;
}

/* Coord confirm. Stamps SHA-256 + writes a confirm version row +
   audits. Bumps the placement to active on first confirm via the legacy
   trigger (kept as-is — the trigger fires when confirmed_at flips). */
export async function confirmLog(logId: string): Promise<DailyLogRow | null> {
  const sessionRes = await supabase.auth.getSession();
  const userId = sessionRes?.data?.session?.user?.id ?? null;

  /* Fetch current row so we hash the exact state being confirmed. */
  const { data: cur } = await supabase
    .from("daily_logs")
    .select("*")
    .eq("id", logId)
    .single();
  if (!cur) {
    console.warn("[dailyLogs.confirmLog] log missing:", logId);
    return null;
  }

  const snapForHash = { ...(cur as DailyLogRow), status: "confirmed" as const };
  const hash = await sha256Hex(snapshotString(snapForHash));
  const nowIso = new Date().toISOString();

  const { data: updated, error } = await supabase
    .from("daily_logs")
    .update({
      status:               "confirmed",
      confirmed_at:         nowIso,
      confirmed_by_user_id: userId,
      sha256_hash:          hash,
    })
    .eq("id", logId)
    .select("*")
    .single();
  if (error || !updated) {
    console.warn("[dailyLogs.confirmLog]:", error);
    return null;
  }
  const row = updated as DailyLogRow;

  await writeVersion({
    log_id:        row.id,
    version:       row.version,
    action:        "confirm",
    snapshot:      row,
    actor_user_id: userId,
  });

  lcAudit({
    event_type: "daily_log.confirmed",
    ui_string:  "Confirmed daily log",
    metadata:   {
      log_id:       row.id,
      placement_id: row.placement_id,
      sha256:       hash,
    },
  });

  return row;
}

/* Coord flag. Bounces the log back to the partner (or to Disputes
   downstream) with a mandatory reason. */
export async function flagLog(logId: string, reason: string): Promise<DailyLogRow | null> {
  const sessionRes = await supabase.auth.getSession();
  const userId = sessionRes?.data?.session?.user?.id ?? null;

  const { data: updated, error } = await supabase
    .from("daily_logs")
    .update({ status: "flagged", flag_reason: reason })
    .eq("id", logId)
    .select("*")
    .single();
  if (error || !updated) {
    console.warn("[dailyLogs.flagLog]:", error);
    return null;
  }
  const row = updated as DailyLogRow;

  await writeVersion({
    log_id:        row.id,
    version:       row.version,
    action:        "flag",
    snapshot:      row,
    reason,
    actor_user_id: userId,
  });

  lcAudit({
    event_type: "daily_log.flagged",
    ui_string:  `Flagged daily log: ${reason}`,
    metadata:   { log_id: row.id, placement_id: row.placement_id, flag_reason: reason },
  });

  return row;
}

/* Lock a confirmed (or deemed-confirmed) log so it becomes the immutable
   payable basis. Stage 1's invoice surface reads locked hours via
   sumLockedHoursForPlacement. */
export async function lockLog(logId: string): Promise<DailyLogRow | null> {
  const sessionRes = await supabase.auth.getSession();
  const userId = sessionRes?.data?.session?.user?.id ?? null;
  const nowIso = new Date().toISOString();

  const { data: cur } = await supabase
    .from("daily_logs")
    .select("*")
    .eq("id", logId)
    .single();
  if (!cur) return null;
  if (!(cur as DailyLogRow).sha256_hash) {
    /* Lock requires a hash — confirm first if somehow missed. */
    const hash = await sha256Hex(snapshotString({ ...(cur as DailyLogRow), status: "locked" as const }));
    await supabase.from("daily_logs").update({ sha256_hash: hash }).eq("id", logId);
  }

  const { data: updated, error } = await supabase
    .from("daily_logs")
    .update({ status: "locked", locked_at: nowIso })
    .eq("id", logId)
    .select("*")
    .single();
  if (error || !updated) {
    console.warn("[dailyLogs.lockLog]:", error);
    return null;
  }
  const row = updated as DailyLogRow;

  await writeVersion({
    log_id:        row.id,
    version:       row.version,
    action:        "lock",
    snapshot:      row,
    actor_user_id: userId,
  });

  lcAudit({
    event_type: "daily_log.locked",
    ui_string:  "Locked daily log",
    metadata:   { log_id: row.id, placement_id: row.placement_id },
  });

  return row;
}

/* Hours total — the verified-hours basis. Sums hours from confirmed,
   deemed_confirmed, and locked rows. Submitted/flagged/draft are NOT
   counted, matching the brief's "confirmed hours are the payable basis"
   principle. */
export async function sumPayableHoursForPlacement(placementId: string): Promise<{
  payable_hours: number;
  locked_hours:  number;
  confirmed_log_count: number;
  locked_log_count:    number;
}> {
  const { data, error } = await supabase
    .from("daily_logs")
    .select("hours_worked, status")
    .eq("placement_id", placementId)
    .in("status", ["confirmed", "deemed_confirmed", "locked"]);
  if (error || !data) {
    return { payable_hours: 0, locked_hours: 0, confirmed_log_count: 0, locked_log_count: 0 };
  }
  let payable = 0;
  let locked  = 0;
  let confirmedCount = 0;
  let lockedCount    = 0;
  for (const r of data as Array<{ hours_worked: number | null; status: LogStatus }>) {
    const h = Number(r.hours_worked || 0);
    payable += h;
    confirmedCount += 1;
    if (r.status === "locked") {
      locked += h;
      lockedCount += 1;
    }
  }
  return {
    payable_hours:       Math.round(payable * 100) / 100,
    locked_hours:        Math.round(locked  * 100) / 100,
    confirmed_log_count: confirmedCount,
    locked_log_count:    lockedCount,
  };
}

export async function listVersionsForLog(logId: string): Promise<Array<{
  id:            string;
  version:       number;
  action:        string;
  snapshot:      unknown;
  sha256:        string;
  reason:        string | null;
  actor_user_id: string | null;
  created_at:    string;
}>> {
  const { data, error } = await supabase
    .from("daily_log_versions")
    .select("*")
    .eq("log_id", logId)
    .order("version", { ascending: true });
  if (error) {
    console.warn("[dailyLogs.listVersionsForLog]:", error);
    return [];
  }
  return (data || []) as never;
}

/* ────────────────────────────────────────────────────────────────────── */
/* v50.265 — Stage 2: exception queue + roll-ups + bulk confirm. */

export interface ExceptionLogRow extends DailyLogRow {
  placement: {
    id:                     string;
    coordinator_company_id: string;
    partner_company_id:     string;
    operative:              { full_name?: string | null; trade?: string | null } | null;
    coord_co:               { name?: string | null } | null;
    partner_co:             { name?: string | null } | null;
    requirement:            { location?: string | null; trade?: string | null } | null;
  } | null;
  /* Why this row appears in the queue. */
  exception_kind: "submitted" | "variance" | "compliance_lapse" | "flagged";
}

export interface ExceptionPage {
  rows:     ExceptionLogRow[];
  total:    number;
  has_more: boolean;
}

/* v50.272 (Stage 7b) — paginated exception fetch via server-side RPC.
   Replaces the previous two-query client-side classify + 500/200 caps.
   The RPC returns the daily_log fields + a server-classified
   exception_kind + a total_rows window count. We then enrich each row
   with placement joins in one secondary batched query so the UI can
   render counterparties + operative names + locations. */
export async function listExceptionsForUser(
  offset: number = 0,
  limit:  number = 100,
): Promise<ExceptionPage> {
  const { data, error } = await supabase.rpc("list_exceptions_paginated", {
    p_limit:  limit,
    p_offset: offset,
  });
  if (error) {
    console.warn("[listExceptionsForUser RPC]:", error);
    return { rows: [], total: 0, has_more: false };
  }
  const pageRows = (data || []) as Array<{
    log_id:                   string;
    placement_id:             string;
    log_date:                 string;
    status:                   LogStatus;
    hours_worked:             number | null;
    planned_hours:            number | null;
    variance_pct:             number | null;
    variance_reason:          string | null;
    flag_reason:              string | null;
    compliance_lapsed:        boolean;
    compliance_lapse_summary: string | null;
    submitted_at:             string | null;
    sha256_hash:              string | null;
    exception_kind:           ExceptionLogRow["exception_kind"];
    total_rows:               number;
  }>;
  if (pageRows.length === 0) {
    return { rows: [], total: 0, has_more: false };
  }
  const total = Number(pageRows[0].total_rows || 0);

  /* Enrichment fetch — pull placement joins for the page's placement_ids
     so the UI can render counterparties + trade + location. */
  const placementIds = Array.from(new Set(pageRows.map(r => r.placement_id).filter(Boolean)));
  const { data: placements } = await supabase
    .from("placements")
    .select(
      "id, coordinator_company_id, partner_company_id, " +
      "operative:operative_id(full_name, trade), " +
      "coord_co:coordinator_company_id(name), " +
      "partner_co:partner_company_id(name), " +
      "requirement:requirement_id(location, trade)",
    )
    .in("id", placementIds);
  const placementById = new Map<string, ExceptionLogRow["placement"]>();
  for (const p of (placements || []) as unknown as Array<{
    id: string;
    coordinator_company_id: string;
    partner_company_id:     string;
    operative:   { full_name?: string | null; trade?: string | null } | null;
    coord_co:    { name?: string | null } | null;
    partner_co:  { name?: string | null } | null;
    requirement: { location?: string | null; trade?: string | null } | null;
  }>) {
    placementById.set(p.id, {
      id: p.id,
      coordinator_company_id: p.coordinator_company_id,
      partner_company_id:     p.partner_company_id,
      operative:              p.operative ?? null,
      coord_co:               p.coord_co ?? null,
      partner_co:             p.partner_co ?? null,
      requirement:            p.requirement ?? null,
    });
  }

  const rows: ExceptionLogRow[] = pageRows.map(r => ({
    id:                       r.log_id,
    placement_id:             r.placement_id,
    log_date:                 r.log_date,
    status:                   r.status,
    hours_worked:             r.hours_worked,
    planned_hours:            r.planned_hours,
    planned_headcount:        null,
    actual_headcount:         null,
    variance_pct:             r.variance_pct,
    variance_reason:          r.variance_reason,
    flag_reason:              r.flag_reason,
    version:                  1,
    sha256_hash:              r.sha256_hash,
    submitted_at:             r.submitted_at,
    submitted_by_user_id:     null,
    confirmed_at:             null,
    confirmed_by_user_id:     null,
    deemed_confirmed_at:      null,
    locked_at:                null,
    compliance_lapsed:        !!r.compliance_lapsed,
    compliance_lapse_summary: r.compliance_lapse_summary,
    notes:                    null,
    created_at:               r.submitted_at || r.log_date,
    placement:                placementById.get(r.placement_id) ?? null,
    exception_kind:           r.exception_kind,
  }));

  return {
    rows,
    total,
    has_more: offset + rows.length < total,
  };
}

export interface WeeklyRollupDay {
  log_date:         string;
  planned_hours:    number;
  actual_hours:     number;
  status:           LogStatus | null;
  variance_pct:     number | null;
  log_id:           string | null;
  in_plan:          boolean;          /* eligible for bulk-confirm */
}

export interface WeeklyRollup {
  placement_id:    string;
  week_start:      string;            /* YYYY-MM-DD, Monday */
  days:            WeeklyRollupDay[];
  total_planned:   number;
  total_actual:    number;
  in_plan_count:   number;
  exception_count: number;
}

/* Monday-aligned week. Returns 7 days starting from weekStart. */
export async function weeklyRollupForPlacement(
  placementId: string,
  weekStart:   string,
): Promise<WeeklyRollup> {
  const { data } = await supabase
    .from("daily_logs")
    .select("id, log_date, status, hours_worked, planned_hours, variance_pct, compliance_lapsed, flag_reason")
    .eq("placement_id", placementId)
    .gte("log_date", weekStart)
    .lt("log_date", addDaysISO(weekStart, 7))
    .order("log_date", { ascending: true });

  const rows = (data || []) as Array<{
    id:               string;
    log_date:         string;
    status:           LogStatus;
    hours_worked:     number | null;
    planned_hours:    number | null;
    variance_pct:     number | null;
    compliance_lapsed:boolean;
    flag_reason:      string | null;
  }>;
  const byDate = new Map<string, typeof rows[number]>();
  for (const r of rows) byDate.set(r.log_date, r);

  const days: WeeklyRollupDay[] = [];
  let totalPlanned = 0;
  let totalActual  = 0;
  let inPlanCount  = 0;
  let exceptionCt  = 0;

  for (let i = 0; i < 7; i++) {
    const dateIso = addDaysISO(weekStart, i);
    const r = byDate.get(dateIso);
    const planned = r?.planned_hours || 0;
    const actual  = r?.hours_worked  || 0;
    totalPlanned += planned;
    totalActual  += actual;
    const isInPlan = !!r
      && r.status === "submitted"
      && (r.variance_pct == null || Math.abs(r.variance_pct) <= 10)
      && !r.compliance_lapsed
      && !r.flag_reason;
    const isException = !!r && (
      (r.status === "submitted" && r.variance_pct != null && Math.abs(r.variance_pct) > 10)
      || r.status === "flagged"
      || r.compliance_lapsed
    );
    if (isInPlan)    inPlanCount += 1;
    if (isException) exceptionCt += 1;
    days.push({
      log_date:      dateIso,
      planned_hours: planned,
      actual_hours:  actual,
      status:        r?.status ?? null,
      variance_pct:  r?.variance_pct ?? null,
      log_id:        r?.id ?? null,
      in_plan:       isInPlan,
    });
  }

  return {
    placement_id:    placementId,
    week_start:      weekStart,
    days,
    total_planned:   Math.round(totalPlanned * 100) / 100,
    total_actual:    Math.round(totalActual  * 100) / 100,
    in_plan_count:   inPlanCount,
    exception_count: exceptionCt,
  };
}

export async function bulkConfirmInPlan(
  placementId: string,
  weekStart:   string,
): Promise<number> {
  const { data, error } = await supabase.rpc("bulk_confirm_in_plan_logs", {
    p_placement_id: placementId,
    p_week_start:   weekStart,
  });
  if (error) {
    console.warn("[dailyLogs.bulkConfirmInPlan]:", error);
    return 0;
  }
  const count = typeof data === "number" ? data : 0;
  lcAudit({
    event_type: "daily_log.bulk_confirmed",
    ui_string:  `Bulk-confirmed ${count} in-plan log${count === 1 ? "" : "s"}`,
    metadata:   { placement_id: placementId, week_start: weekStart, count },
  });
  return count;
}

export interface ProjectRollupEntry {
  /* Group keys depend on groupBy: for "partner+trade" the entity is the
     partner company; for "coord+project" it's the coord company. */
  entity_id:       string;
  entity_name:     string | null;
  trade_or_proj:   string | null;
  payable_hours:   number;
  locked_hours:    number;
  exception_count: number;
}

export type RollupGroupBy = "partner+trade" | "coord+project";

/* Project-level rollup. v50.271 (Stage 7) — routes through the
   server-side compute_project_rollup RPC instead of pulling every
   visible daily_logs row over the wire. Caller can pick the grouping
   axis (partner+trade for coord, coord+project for partner). */
export async function projectRollup(
  projectId: string | number,
  groupBy:   RollupGroupBy = "partner+trade",
): Promise<ProjectRollupEntry[]> {
  void projectId;
  const { data, error } = await supabase.rpc("compute_project_rollup", {
    p_group_by: groupBy,
    p_limit:    100,
  });
  if (error) {
    console.warn("[dailyLogs.projectRollup RPC]:", error);
    return [];
  }
  return ((data || []) as Array<{
    entity_id:       string;
    entity_name:     string | null;
    trade_or_proj:   string | null;
    payable_hours:   number;
    locked_hours:    number;
    exception_count: number;
  }>).map(r => ({
    entity_id:       r.entity_id,
    entity_name:     r.entity_name,
    trade_or_proj:   r.trade_or_proj === "—" ? null : r.trade_or_proj,
    payable_hours:   Number(r.payable_hours || 0),
    locked_hours:    Number(r.locked_hours  || 0),
    exception_count: Number(r.exception_count || 0),
  }));
}

/* Helper — add N days to a YYYY-MM-DD string. */
function addDaysISO(iso: string, days: number): string {
  const d = new Date(iso + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/* ────────────────────────────────────────────────────────────────────── */
/* v50.270 — Stage 6: partner-side actions. amend + bulk-submit-today +
   payment readiness rollup. */

interface AmendInput {
  actual_hours:     number;
  actual_headcount: number;
  variance_reason?: string | null;
  notes?:           string | null;
  amend_reason:     string;   /* why partner is re-submitting */
}

/* Amend a flagged log in place — updates the row with new values, sets
   status back to submitted, bumps version, writes an 'amend' version
   row + a fresh sha256 stamp. Used by the partner-side inline action
   on the Exception queue. */
export async function amendFlaggedLog(logId: string, input: AmendInput): Promise<DailyLogRow | null> {
  const sessionRes = await supabase.auth.getSession();
  const userId = sessionRes?.data?.session?.user?.id ?? null;

  const { data: cur } = await supabase
    .from("daily_logs")
    .select("*")
    .eq("id", logId)
    .single();
  if (!cur) return null;
  const curRow = cur as DailyLogRow;

  const nextVersion = (curRow.version || 1) + 1;
  const snapForHash = {
    ...curRow,
    hours_worked:     input.actual_hours,
    actual_headcount: input.actual_headcount,
    variance_reason:  input.variance_reason ?? null,
    notes:            input.notes ?? null,
    status:           "submitted" as const,
    version:          nextVersion,
  };
  const hash = await sha256Hex(snapshotString(snapForHash));

  const { data: updated, error } = await supabase
    .from("daily_logs")
    .update({
      status:              "submitted",
      hours_worked:        input.actual_hours,
      actual_headcount:    input.actual_headcount,
      variance_reason:     input.variance_reason ?? null,
      notes:               input.notes ?? null,
      flag_reason:         null,        /* clear the flag so it leaves the exception queue */
      version:             nextVersion,
      sha256_hash:         hash,
      submitted_at:        new Date().toISOString(),
      submitted_by_user_id:userId,
      /* Reset confirm-side stamps so the coord re-runs through the same
         confirm/flag path; deemed-confirm clock starts over. */
      confirmed_at:        null,
      confirmed_by_user_id:null,
      deemed_confirmed_at: null,
      nudged_at:           null,
    })
    .eq("id", logId)
    .select("*")
    .single();
  if (error || !updated) {
    console.warn("[dailyLogs.amendFlaggedLog]:", error);
    return null;
  }
  const row = updated as DailyLogRow;

  await writeVersion({
    log_id:        row.id,
    version:       nextVersion,
    action:        "amend",
    snapshot:      row,
    reason:        input.amend_reason,
    actor_user_id: userId,
  });

  lcAudit({
    event_type: "daily_log.amended",
    ui_string:  "Amended flagged log + resubmitted",
    metadata:   {
      log_id:        row.id,
      placement_id:  row.placement_id,
      version:       nextVersion,
      amend_reason:  input.amend_reason,
    },
  });

  return row;
}

interface MissingTodayPlacement {
  placement_id:   string;
  operative_id:   string | null;
  operative_name: string | null;
}

/* Returns the partner's planned placements that don't yet have a log
   row for the given date. v50.273 (Stage 8) — widened stage filter to
   the "deal locked" set so contracted/introduced/pending_onsite
   placements also surface in the missing-today banner. */
export async function listPlacementsMissingLog(logDate: string): Promise<MissingTodayPlacement[]> {
  const sessionRes = await supabase.auth.getSession();
  const userId = sessionRes?.data?.session?.user?.id ?? null;
  if (!userId) return [];

  interface RawPlacementRow {
    id: string;
    operative_id: string | null;
    operative: { full_name: string | null } | null;
  }
  const { data: placements } = await supabase
    .from("placements")
    .select("id, operative_id, operative:operative_id(full_name)")
    .in("stage", ["contracted", "introduced", "pending_onsite", "active"])
    .is("deleted_at", null)
    .limit(200);
  const all = ((placements || []) as unknown as RawPlacementRow[]).map(p => ({
    placement_id:   p.id,
    operative_id:   p.operative_id,
    operative_name: p.operative?.full_name ?? null,
  }));
  if (all.length === 0) return [];

  const { data: existingLogs } = await supabase
    .from("daily_logs")
    .select("placement_id")
    .eq("log_date", logDate)
    .in("placement_id", all.map(p => p.placement_id));
  const haveLog = new Set(
    ((existingLogs || []) as Array<{ placement_id: string }>).map(r => r.placement_id),
  );
  return all.filter(p => !haveLog.has(p.placement_id));
}

/* Bulk submit "as planned". v50.271 (Stage 7) — single-transaction
   server-side RPC replaces the previous N-round-trip client loop.
   Inserts daily_logs + daily_log_versions rows for every partner-
   company placement that's missing the date in one go. */
export async function bulkSubmitMissingToday(logDate: string): Promise<number> {
  const { data, error } = await supabase.rpc("bulk_submit_today_for_my_company", {
    p_log_date: logDate,
  });
  if (error) {
    console.warn("[bulkSubmitMissingToday RPC]:", error);
    return 0;
  }
  const count = typeof data === "number" ? data : 0;
  if (count > 0) {
    lcAudit({
      event_type: "daily_log.bulk_submitted",
      ui_string:  `Submitted ${count} log${count === 1 ? "" : "s"} as planned`,
      metadata:   { log_date: logDate, count },
    });
  }
  return count;
}

/* List the distinct site locations the caller can see across their
   placements. Powers the Stage 7 location filter chip. */
export async function listDistinctLocations(): Promise<string[]> {
  const { data, error } = await supabase.rpc("list_distinct_locations_for_my_placements");
  if (error) {
    console.warn("[listDistinctLocations]:", error);
    return [];
  }
  return ((data || []) as Array<{ location: string }>)
    .map(r => r.location)
    .filter(Boolean);
}

export interface PaymentReadinessRow {
  placement_id:        string;
  operative_name:      string | null;
  trade:               string | null;
  coord_name:          string | null;
  agreed_day_rate:     number | null;
  locked_hours:        number;
  payable_pence:       number;            /* locked_hours * agreed_day_rate / 10 (standard day) */
  invoiced_hours_est:  number;            /* rough estimate from invoiced milestones */
  ready_to_invoice:    boolean;
}

export interface PaymentReadinessPage {
  rows:     PaymentReadinessRow[];
  total:    number;
  has_more: boolean;
}

/* v50.272 (Stage 7b) — paginated partner payment-readiness rollup via
   server-side RPC. The SQL handles the locked-hours sum + invoiced
   subtraction + ready_to_invoice classification in one query. */
export async function listPartnerPaymentReadinessPaginated(
  offset: number = 0,
  limit:  number = 50,
): Promise<PaymentReadinessPage> {
  const { data, error } = await supabase.rpc("list_partner_payment_readiness_paginated", {
    p_limit:  limit,
    p_offset: offset,
  });
  if (error) {
    console.warn("[listPartnerPaymentReadinessPaginated]:", error);
    return { rows: [], total: 0, has_more: false };
  }
  const pageRows = (data || []) as Array<{
    placement_id:    string;
    operative_name:  string | null;
    trade:           string | null;
    coord_name:      string | null;
    agreed_day_rate: number | null;
    locked_hours:    number;
    payable_pence:   number;
    invoiced_pence:  number;
    ready_to_invoice: boolean;
    total_rows:      number;
  }>;
  if (pageRows.length === 0) {
    return { rows: [], total: 0, has_more: false };
  }
  const total = Number(pageRows[0].total_rows || 0);
  const rows: PaymentReadinessRow[] = pageRows.map(r => {
    const rate = Number(r.agreed_day_rate || 0);
    const invoicedPence = Number(r.invoiced_pence || 0);
    const invoicedHoursEst = rate > 0
      ? Math.round((invoicedPence / 100 / rate) * 10 * 100) / 100
      : 0;
    return {
      placement_id:       r.placement_id,
      operative_name:     r.operative_name,
      trade:              r.trade,
      coord_name:         r.coord_name,
      agreed_day_rate:    rate || null,
      locked_hours:       Number(r.locked_hours || 0),
      payable_pence:      Number(r.payable_pence || 0),
      invoiced_hours_est: invoicedHoursEst,
      ready_to_invoice:   !!r.ready_to_invoice,
    };
  });
  return {
    rows,
    total,
    has_more: offset + rows.length < total,
  };
}

/* Legacy non-paginated wrapper kept so callers that haven't moved to
   the page-based shape still work. Returns the first 100. */
export async function listPartnerPaymentReadiness(): Promise<PaymentReadinessRow[]> {
  const page = await listPartnerPaymentReadinessPaginated(0, 100);
  return page.rows;
}

/* OLD client-side implementation kept inline as a comment for the next
   time someone wonders why the join shape changed. */
async function _legacyListPartnerPaymentReadiness(): Promise<PaymentReadinessRow[]> {
  /* Fetch placements (RLS gates to ones the partner can see). */
  interface PlacementJoin {
    id: string;
    agreed_day_rate: number | null;
    coordinator_company_id: string;
    operative: { full_name: string | null; trade: string | null } | null;
    coord_co:  { name: string | null } | null;
  }
  const { data: placementsRaw } = await supabase
    .from("placements")
    .select("id, agreed_day_rate, coordinator_company_id, operative:operative_id(full_name, trade), coord_co:coordinator_company_id(name)")
    .is("deleted_at", null)
    .limit(200);
  const placements = (placementsRaw || []) as unknown as PlacementJoin[];
  if (placements.length === 0) return [];

  const placementIds = placements.map(p => p.id);

  /* Sum locked hours per placement. */
  const { data: lockedLogs } = await supabase
    .from("daily_logs")
    .select("placement_id, hours_worked")
    .in("placement_id", placementIds)
    .in("status", ["confirmed", "deemed_confirmed", "locked"]);
  const lockedByPlacement = new Map<string, number>();
  for (const r of (lockedLogs || []) as Array<{ placement_id: string; hours_worked: number | null }>) {
    lockedByPlacement.set(
      r.placement_id,
      (lockedByPlacement.get(r.placement_id) || 0) + Number(r.hours_worked || 0),
    );
  }

  /* Rough invoiced estimate — sum value of invoiced milestones for each
     placement that are status='sent' or 'paid'. We can't precisely
     translate £ back to hours without knowing day-rate breakdown, so
     this is a coarse signal; the UI labels it as approximate. */
  const { data: invs } = await supabase
    .from("invoices")
    .select("placement_id, status, total_pence")
    .in("placement_id", placementIds)
    .in("status", ["sent", "paid"]);
  const invoicedPenceByPlacement = new Map<string, number>();
  for (const r of (invs || []) as Array<{ placement_id: string; status: string; total_pence: number | null }>) {
    invoicedPenceByPlacement.set(
      r.placement_id,
      (invoicedPenceByPlacement.get(r.placement_id) || 0) + Number(r.total_pence || 0),
    );
  }

  return placements.map(p => {
    const lockedHours = Math.round((lockedByPlacement.get(p.id) || 0) * 100) / 100;
    const rate = Number(p.agreed_day_rate || 0);
    /* Standard 10-hour day → hours / 10 × rate × 100 pence. */
    const payablePence = Math.round((lockedHours / 10) * rate * 100);
    const invoicedPence = invoicedPenceByPlacement.get(p.id) || 0;
    const invoicedHoursEst = rate > 0
      ? Math.round((invoicedPence / 100 / rate) * 10 * 100) / 100
      : 0;
    const readyToInvoice = lockedHours > invoicedHoursEst + 0.1;
    return {
      placement_id:       p.id,
      operative_name:     p.operative?.full_name ?? null,
      trade:              p.operative?.trade ?? null,
      coord_name:         p.coord_co?.name ?? null,
      agreed_day_rate:    rate || null,
      locked_hours:       lockedHours,
      payable_pence:      payablePence,
      invoiced_hours_est: invoicedHoursEst,
      ready_to_invoice:   readyToInvoice,
    };
  }).filter(r => r.locked_hours > 0)
    .sort((a, b) => Number(b.ready_to_invoice) - Number(a.ready_to_invoice) || b.payable_pence - a.payable_pence);
}
void _legacyListPartnerPaymentReadiness;

/* ────────────────────────────────────────────────────────────────────── */
/* v50.273 — Stage 8: Daily Roster data source. */

export interface PlannedPlacementRow {
  placement_id:             string;
  operative_id:             string | null;
  operative_name:           string | null;
  trade:                    string | null;
  partner_company_id:       string | null;
  partner_company_name:     string | null;
  coordinator_company_id:   string | null;
  coordinator_company_name: string | null;
  location:                 string | null;
  /* v50.281 — project grouping context. project_id null on legacy
     placements where requirement.project_id wasn't set; the UI falls
     back to grouping by location in that case. */
  project_id:               string | null;
  project_name:             string | null;
  client_name:              string | null;
  stage:                    string;
  start_date:               string | null;
  agreed_day_rate:          number | null;
  today_log_id:             string | null;
  today_log_status:         string | null;
  today_log_flag_reason:    string | null;
  viewer_side:              "partner" | "coordinator" | null;
}

export interface PlannedPlacementsPage {
  rows:     PlannedPlacementRow[];
  total:    number;
  has_more: boolean;
}

export async function listPlannedPlacements(
  lookaheadDays: number = 30,
  offset:        number = 0,
  limit:         number = 100,
): Promise<PlannedPlacementsPage> {
  const { data, error } = await supabase.rpc("list_planned_placements_for_user", {
    p_lookahead_days: lookaheadDays,
    p_limit:          limit,
    p_offset:         offset,
  });
  if (error) {
    console.warn("[listPlannedPlacements]:", error);
    return { rows: [], total: 0, has_more: false };
  }
  const pageRows = (data || []) as Array<PlannedPlacementRow & { total_rows: number }>;
  if (pageRows.length === 0) {
    return { rows: [], total: 0, has_more: false };
  }
  const total = Number(pageRows[0].total_rows || 0);
  const rows: PlannedPlacementRow[] = pageRows.map(({ total_rows: _t, ...rest }) => rest);
  return {
    rows,
    total,
    has_more: offset + rows.length < total,
  };
}

/* Helper — compute the status pill kind + label for a roster row. */
export type RosterStatusKind =
  | "flagged"
  | "missing"
  | "mobilising_today"
  | "mobilising_soon"
  | "logged"
  | "completed"
  | "draft";

export function rosterStatusFor(row: PlannedPlacementRow): { kind: RosterStatusKind; label: string } {
  const today = new Date().toISOString().slice(0, 10);
  const start = row.start_date;
  if (row.today_log_status === "flagged") {
    return { kind: "flagged", label: "Flagged · needs amend" };
  }
  if (row.stage === "active" && !row.today_log_id) {
    return { kind: "missing", label: "On site · LOG MISSING" };
  }
  if (start && start === today && !row.today_log_id) {
    return { kind: "mobilising_today", label: "Mobilising today · pre-fill ready" };
  }
  if (start && start > today) {
    const ms = Date.parse(start) - Date.parse(today);
    const days = Math.max(1, Math.round(ms / 86_400_000));
    return { kind: "mobilising_soon", label: `Mobilising in ${days}d` };
  }
  if (row.today_log_status && ["submitted", "confirmed", "deemed_confirmed", "locked"].includes(row.today_log_status)) {
    return { kind: "logged", label: "Logged today" };
  }
  if (row.stage === "completed") {
    return { kind: "completed", label: "Completed" };
  }
  return { kind: "draft", label: "Awaiting first mobilise" };
}

/* Returns the Monday-aligned ISO date for a given date string. Used by
   the weekly-return view to snap to a stable week. */
export function isoMonday(iso: string): string {
  const d = new Date(iso + "T00:00:00Z");
  const day = d.getUTCDay();             /* 0=Sun, 1=Mon, ..., 6=Sat */
  const diff = day === 0 ? -6 : 1 - day;
  d.setUTCDate(d.getUTCDate() + diff);
  return d.toISOString().slice(0, 10);
}

/* ────────────────────────────────────────────────────────────────────── */
/* v50.266 — Stage 3: exception attachments + milestone hours progress. */

export interface LogAttachment {
  id:                   string;
  log_id:               string;
  file_path:            string;
  file_name:            string;
  file_size:            number | null;
  mime_type:            string | null;
  kind:                 "note" | "photo" | "doc" | "other";
  note:                 string | null;
  uploaded_by_user_id:  string | null;
  created_at:           string;
}

/* Lists current (non-deleted) attachments for a log. RLS gates the
   visibility to either placement party. */
export async function listAttachmentsForLog(logId: string): Promise<LogAttachment[]> {
  const { data, error } = await supabase
    .from("daily_log_attachments")
    .select("*")
    .eq("log_id", logId)
    .is("deleted_at", null)
    .order("created_at", { ascending: false });
  if (error) {
    console.warn("[dailyLogs.listAttachmentsForLog]:", error);
    return [];
  }
  return (data || []) as LogAttachment[];
}

interface AddAttachmentInput {
  log_id: string;
  note:   string;
  file?:  File | null;
  kind?:  LogAttachment["kind"];
}

/* Adds an exception attachment to a log. Uploads the (optional) file to
   site-ops-attachments first, then writes the daily_log_attachments
   row. The BEFORE INSERT trigger rejects if the log isn't in an
   exception state — surface that error to the caller. Note-only
   attachments (no file) are allowed by passing file=null and kind='note'. */
export async function addExceptionAttachment(input: AddAttachmentInput): Promise<LogAttachment | null> {
  const sessionRes = await supabase.auth.getSession();
  const userId = sessionRes?.data?.session?.user?.id ?? null;

  let filePath = "";
  let fileName = "";
  let fileSize: number | null = null;
  let mimeType: string | null = null;

  if (input.file) {
    const safe = input.file.name.replace(/[^A-Za-z0-9_.\-]/g, "_");
    const ts   = Date.now();
    filePath   = `${input.log_id}/${ts}-${safe}`;
    fileName   = input.file.name;
    fileSize   = input.file.size;
    mimeType   = input.file.type || "application/octet-stream";

    const { error: upErr } = await supabase.storage
      .from("site-ops-attachments")
      .upload(filePath, input.file, { contentType: mimeType });
    if (upErr) {
      console.warn("[dailyLogs.addExceptionAttachment upload]:", upErr);
      throw new Error(upErr.message || "Couldn't upload the file.");
    }
  }

  const insertRow = {
    log_id:              input.log_id,
    file_path:           filePath,
    file_name:           fileName || (input.note.trim().slice(0, 60) || "Note"),
    file_size:           fileSize,
    mime_type:           mimeType,
    kind:                input.kind ?? (input.file ? "photo" : "note"),
    note:                input.note.trim() || null,
    uploaded_by_user_id: userId,
  };

  const { data, error } = await supabase
    .from("daily_log_attachments")
    .insert(insertRow)
    .select("*")
    .single();
  if (error) {
    console.warn("[dailyLogs.addExceptionAttachment insert]:", error);
    /* If the file got uploaded but the row insert failed (e.g. the
       trigger fired because the log isn't an exception anymore), the
       file's orphaned in the bucket. Best-effort cleanup. */
    if (filePath) {
      try {
        await supabase.storage.from("site-ops-attachments").remove([filePath]);
      } catch { /* ignore */ }
    }
    throw new Error(error.message || "Couldn't attach to this log.");
  }

  lcAudit({
    event_type: "daily_log.attachment_added",
    ui_string:  `Attached ${insertRow.kind} to exception log`,
    metadata:   { log_id: input.log_id, kind: insertRow.kind, has_file: !!input.file },
  });

  return data as LogAttachment;
}

export async function deleteAttachment(attachmentId: string): Promise<boolean> {
  const { error } = await supabase
    .from("daily_log_attachments")
    .update({ deleted_at: new Date().toISOString() })
    .eq("id", attachmentId);
  if (error) {
    console.warn("[dailyLogs.deleteAttachment]:", error);
    return false;
  }
  return true;
}

/* Returns a short-lived signed URL for an attachment file. */
export async function getAttachmentDownloadUrl(filePath: string): Promise<string | null> {
  if (!filePath) return null;
  const { data, error } = await supabase.storage
    .from("site-ops-attachments")
    .createSignedUrl(filePath, 300);   /* 5 minutes — plenty for a click-through. */
  if (error) {
    console.warn("[dailyLogs.getAttachmentDownloadUrl]:", error);
    return null;
  }
  return data?.signedUrl ?? null;
}

/* ────────────────────────────────────────────────────────────────────── */
/* v50.267 — Stage 4: saved views + per-placement audit export. */

export interface SavedView {
  id:          string;
  tab:         string;
  name:        string;
  params:      Record<string, unknown>;
  created_at:  string;
  updated_at:  string;
}

export async function listMySavedViews(tab: string): Promise<SavedView[]> {
  const { data, error } = await supabase
    .from("saved_site_ops_views")
    .select("id, tab, name, params, created_at, updated_at")
    .eq("tab", tab)
    .is("deleted_at", null)
    .order("name", { ascending: true });
  if (error) {
    console.warn("[dailyLogs.listMySavedViews]:", error);
    return [];
  }
  return (data || []) as SavedView[];
}

export async function createSavedView(
  tab:    string,
  name:   string,
  params: Record<string, unknown>,
): Promise<SavedView | null> {
  const sessionRes = await supabase.auth.getSession();
  const userId = sessionRes?.data?.session?.user?.id ?? null;
  if (!userId) return null;

  const { data, error } = await supabase
    .from("saved_site_ops_views")
    .insert({ user_id: userId, tab, name: name.trim(), params })
    .select("id, tab, name, params, created_at, updated_at")
    .single();
  if (error) {
    console.warn("[dailyLogs.createSavedView]:", error);
    return null;
  }
  return data as SavedView;
}

export async function deleteSavedView(viewId: string): Promise<boolean> {
  const { error } = await supabase
    .from("saved_site_ops_views")
    .update({ deleted_at: new Date().toISOString() })
    .eq("id", viewId);
  if (error) {
    console.warn("[dailyLogs.deleteSavedView]:", error);
    return false;
  }
  return true;
}

/* ────────────────────────────────────────────────────────────────────── */
/* Audit export — CSV + PDF of a placement's signed log history with the
   full version chain per log. */

export interface AuditExportLogRow {
  log:      DailyLogRow;
  versions: Array<{
    version:       number;
    action:        string;
    sha256:        string;
    reason:        string | null;
    actor_user_id: string | null;
    created_at:    string;
  }>;
}

/* Fetches every log on the placement + the full version chain per log.
   Sorted by log_date ascending so the audit reads chronologically. */
export async function fetchPlacementAuditTrail(placementId: string): Promise<AuditExportLogRow[]> {
  const [logsR, versionsR] = await Promise.all([
    supabase
      .from("daily_logs")
      .select("*")
      .eq("placement_id", placementId)
      .order("log_date", { ascending: true })
      .limit(500),
    supabase
      .from("daily_log_versions")
      .select("log_id, version, action, sha256, reason, actor_user_id, created_at")
      .order("created_at", { ascending: true }),
  ]);
  if (logsR.error) console.warn("[fetchPlacementAuditTrail logs]:", logsR.error);
  if (versionsR.error) console.warn("[fetchPlacementAuditTrail versions]:", versionsR.error);

  const logs = (logsR.data || []) as unknown as DailyLogRow[];
  const versionsByLog = new Map<string, AuditExportLogRow["versions"]>();
  for (const v of (versionsR.data || []) as unknown as Array<{
    log_id:        string;
    version:       number;
    action:        string;
    sha256:        string;
    reason:        string | null;
    actor_user_id: string | null;
    created_at:    string;
  }>) {
    let arr = versionsByLog.get(v.log_id);
    if (!arr) { arr = []; versionsByLog.set(v.log_id, arr); }
    arr.push({
      version:       v.version,
      action:        v.action,
      sha256:        v.sha256,
      reason:        v.reason,
      actor_user_id: v.actor_user_id,
      created_at:    v.created_at,
    });
  }

  return logs.map(log => ({
    log,
    versions: versionsByLog.get(log.id) || [],
  }));
}

export interface MilestoneWithProgress {
  id:                    string;
  position:              number;
  description:           string;
  agreed_date:           string | null;
  value_pence:           number;
  status:                "pending" | "evidence_submitted" | "met" | "deemed_met" | "disputed";
  target_hours:          number | null;
  auto_progressed_at:    string | null;
  evidence_submitted_at: string | null;
  marked_met_at:         string | null;
  deemed_met_at:         string | null;
  /* v50.266 — hours roll-up so the UI can render the progress bar. */
  confirmed_hours:       number;
  hours_progress_pct:    number | null;
  ready_to_invoice:      boolean;
}

/* Returns the placement's milestones enriched with confirmed-hours
   progress + a ready_to_invoice hint. The hint fires when status hits
   evidence_submitted/met/deemed_met — i.e. the hours-driven trigger
   pushed it past the threshold OR the coord marked met manually. */
export async function listMilestonesWithProgress(placementId: string): Promise<MilestoneWithProgress[]> {
  const [milestonesR, hoursR] = await Promise.all([
    supabase
      .from("placement_milestones")
      .select("id, position, description, agreed_date, value_pence, status, target_hours, auto_progressed_at, evidence_submitted_at, marked_met_at, deemed_met_at")
      .eq("placement_id", placementId)
      .order("position", { ascending: true }),
    supabase
      .from("daily_logs")
      .select("hours_worked, status")
      .eq("placement_id", placementId)
      .in("status", ["confirmed", "deemed_confirmed", "locked"]),
  ]);
  if (milestonesR.error) {
    console.warn("[dailyLogs.listMilestonesWithProgress milestones]:", milestonesR.error);
    return [];
  }

  let confirmedHours = 0;
  for (const r of (hoursR.data || []) as Array<{ hours_worked: number | null; status: string }>) {
    confirmedHours += Number(r.hours_worked || 0);
  }

  return ((milestonesR.data || []) as unknown as Array<{
    id:                    string;
    position:              number;
    description:           string;
    agreed_date:           string | null;
    value_pence:           number;
    status:                MilestoneWithProgress["status"];
    target_hours:          number | null;
    auto_progressed_at:    string | null;
    evidence_submitted_at: string | null;
    marked_met_at:         string | null;
    deemed_met_at:         string | null;
  }>).map(m => {
    const pct = m.target_hours && m.target_hours > 0
      ? Math.min(100, Math.round((confirmedHours / m.target_hours) * 100))
      : null;
    const readyToInvoice =
      m.status === "evidence_submitted"
      || m.status === "met"
      || m.status === "deemed_met";
    return {
      ...m,
      confirmed_hours:    Math.round(confirmedHours * 100) / 100,
      hours_progress_pct: pct,
      ready_to_invoice:   readyToInvoice,
    };
  });
}
